import React, { useState } from 'react';
import { Typography, Box, Avatar, Chip } from '@mui/material';
import { Circle as CircleIcon, EditNote as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return parts.length ? (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() : '?';
};

/** A co-author saved changes to a manuscript you work on (one rolled-up card per editor). */
export default function ManuscriptEditNotificationCard({ notification, onMarkRead }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const p = notification.payload || {};
  const [broken, setBroken] = useState(false);
  const isPublication = p.document_type === 'publication';
  const avatar = !broken && (p.author_avatar_url || null);
  const name = p.proposal_acronym || p.proposal_title || 'a manuscript';
  const sections = p.sections || [];
  const saves = Number(p.edit_count || 1);

  const open = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (p.consortium_id && p.proposal_id) {
      navigate(`/consortium/${p.consortium_id}/${isPublication ? 'publications' : 'proposals'}/${p.proposal_id}/`);
    }
  };

  return (
    <Box
      onClick={open}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer', '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar src={avatar || undefined} onError={() => setBroken(true)}
        sx={{ width: 34, height: 34, mt: 0.25, fontSize: 13, bgcolor: theme.palette.primary.avatar, color: 'white' }}>
        {p.author_name ? initials(p.author_name) : <EditIcon sx={{ fontSize: 18 }} />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <Box component="span" fontWeight={600}>{p.author_name || 'A co-author'}</Box>
            <Box component="span" color="text.secondary"> edited </Box>
            <Box component="span" fontWeight={600}>{name}</Box>
          </Typography>
          <Chip size="small" label={isPublication ? 'Publication' : 'Proposal'}
            sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600,
                  bgcolor: isPublication ? 'rgba(15,118,110,0.1)' : 'rgba(124,58,237,0.1)',
                  color: isPublication ? '#0f766e' : '#7c3aed' }} />
        </Box>
        {p.proposal_acronym && p.proposal_title && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>{p.proposal_title}</Typography>
        )}
        {sections.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
            {sections.map((s) => (
              <Chip key={s} size="small" variant="outlined" label={s.length > 28 ? `${s.slice(0, 28)}…` : s}
                sx={{ height: 20, fontSize: '0.72rem' }} />
            ))}
            {saves > 1 && (
              <Typography variant="caption" color="text.secondary">· {saves} saves</Typography>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
